import { useState, type ReactElement, type ReactNode } from "react";
import { Mail, MessageCircle, Settings2 } from "lucide-react";
import { ButtonLink, Card } from "@/components/play";
import { Accordion } from "@/components/play/Accordion";
import { DiscordSupportLink } from "@/components/DiscordSupportLink";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { SitePage } from "@/components/SitePage";
import faqData from "../data/faq.json";

interface FAQ {
  id: string;
  question: string;
  slug: string;
  answer: string;
}

const toItems = (faqs: FAQ[]) =>
  faqs.map((faq) => ({ value: faq.id, title: faq.question, content: <p>{faq.answer}</p> }));

const generalItems = toItems(faqData.faqs);
const technicalItems = toItems(faqData.technicalFaqs);

/** A section heading with its Lucide glyph in a neutral disc. */
function SectionHeading({ id, icon, children }: { id: string; icon: ReactElement; children: ReactNode }) {
  return (
    <div className="mb-8 flex items-center gap-3">
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-neutral-2 [&>svg]:h-5 [&>svg]:w-5" aria-hidden="true">
        {icon}
      </span>
      <h2 id={id} className="text-xl font-black text-neutral-10">{children}</h2>
    </div>
  );
}

export function FAQPage() {
  useDocumentTitle("FAQ · StreamBridge");
  // One answer open across both sections: each accordion shows the open answer only when it holds it.
  const [openId, setOpenId] = useState<string | null>(null);
  const openIn = (faqs: FAQ[]) => (openId && faqs.some((faq) => faq.id === openId) ? [openId] : []);
  const toggle = (open: string[]) => setOpenId(open[0] ?? null);

  return (
    <SitePage mainId="faq-main">
      <section className="px-6 py-24">
        <div className="mx-auto max-w-4xl text-center">
          <p className="mb-6 text-size-xs font-medium text-neutral-7">FAQ</p>
          <h1 className="mb-6 text-4xl font-black text-neutral-10 sm:text-5xl">Frequently asked questions</h1>
          <p className="mx-auto max-w-2xl text-lg text-neutral-7">
            Can’t find your answer?{" "}
            <a href="mailto:hello@streambridge.live" className="text-neutral-10 underline underline-offset-4 hover:text-neutral-8">
              Email the team
            </a>.
          </p>
        </div>
      </section>

      <section className="px-6 pb-16" aria-labelledby="faq-general-title">
        <div className="mx-auto max-w-3xl">
          <SectionHeading id="faq-general-title" icon={<MessageCircle />}>Getting started</SectionHeading>
          <Accordion items={generalItems} value={openIn(faqData.faqs)} onValueChange={toggle} />
        </div>
      </section>

      <section className="px-6 pb-16" aria-labelledby="faq-technical-title">
        <div className="mx-auto max-w-3xl">
          <SectionHeading id="faq-technical-title" icon={<Settings2 />}>Cameras, SRT and OBS</SectionHeading>
          <Accordion items={technicalItems} value={openIn(faqData.technicalFaqs)} onValueChange={toggle} />
        </div>
      </section>

      <section className="px-6 pb-24">
        <div className="mx-auto max-w-4xl">
          <Card
            title="Still have questions?"
            description="Ask our crew on Discord, or email the team."
            actions={
              <>
                <DiscordSupportLink />
                <ButtonLink href="mailto:hello@streambridge.live" variant="secondary">
                  <Mail aria-hidden="true" />
                  Contact us
                </ButtonLink>
              </>
            }
          />
        </div>
      </section>
    </SitePage>
  );
}

export default FAQPage;
